import { z } from 'zod';

/**
 * ADR-4 — a project carries THREE independent state dimensions. Collapsing them
 * into one enum produces impossible combinations (IDEA + SHOOTING, ARCHIVED +
 * BOOKING), and collapsing derived onto asserted loses the ability to say whether
 * a status was calculated or claimed by a human.
 */

/**
 * WHAT A PROJECT IS, in the product's own words.
 *
 * Shared as a constant rather than retyped per screen because a definition
 * that is written twice is written differently the second time, and this one
 * carries the mental model the whole workspace rests on: a production is the
 * thing being made, a project is where the work on it happens.
 *
 * "WORKSPACE FOR", never "holds". The copy this replaced said a project HOLDS
 * a production — which describes a database row, invites a producer to read it
 * as a folder, and then leaves them looking for their files in a workspace they
 * did not know they had. (Owner decision, 2026-09-21.)
 */
export const PROJECT_DEFINITION = 'A project is your workspace for one production.';

/** Producer-controlled maturity. THIS drives the phase-adaptive navigation. */
export const LifecyclePhase = z.enum([
  'IDEA',
  'DEVELOPMENT',
  'PRE_PRODUCTION',
  'PRODUCTION',
  'WRAP',
  'ARCHIVED',
]);
export type LifecyclePhase = z.infer<typeof LifecyclePhase>;

/**
 * One spelling of each phase, for the same reason the formats have one: this
 * map existed twice already — in `StatusTag` and in `ProjectStatePanel` — and
 * a third copy was about to be written for the creation page.
 */
export const LIFECYCLE_PHASE_LABELS: Record<LifecyclePhase, string> = {
  IDEA: 'Idea',
  DEVELOPMENT: 'Development',
  PRE_PRODUCTION: 'Pre-production',
  PRODUCTION: 'Production',
  WRAP: 'Wrap',
  ARCHIVED: 'Archived',
};

/**
 * The label for a phase that arrived as a plain string.
 *
 * The transitions endpoint types its phases as `string`, deliberately: the
 * column is a varchar and a newer deploy's value reaches an older SPA
 * mid-rollout — the same reason `roleHasPermission` exists rather than
 * indexing the permission matrix directly. Indexing the map with it would be
 * `undefined` on screen; this shows the raw value, which is ugly and true.
 */
export function lifecyclePhaseLabel(phase: string): string {
  const labels: Readonly<Record<string, string | undefined>> = LIFECYCLE_PHASE_LABELS;

  return labels[phase] ?? phase;
}

/**
 * The phases a production can be created IN.
 *
 * Archived is not one of them — a production being created has not finished —
 * and it is excluded here rather than at each call site so that "everything
 * except archived" cannot come to mean different things in two places.
 */
export const CREATABLE_LIFECYCLE_PHASES = LifecyclePhase.options.filter(
  (phase) => phase !== 'ARCHIVED',
);

/** Where the work actually sits. System-suggested, user-overridable. */
export const OperationalPhase = z.enum([
  'DRAFT',
  'ANALYSIS',
  'PLANNING',
  'SCOUTING',
  'SHORTLISTING',
  'BOOKING',
  'PREPARING',
  'SHOOTING',
  'WRAP',
  'COMPLETED',
  'ARCHIVED',
]);
export type OperationalPhase = z.infer<typeof OperationalPhase>;

export const ProjectHealth = z.enum(['ON_TRACK', 'AT_RISK', 'BLOCKED']);
export type ProjectHealth = z.infer<typeof ProjectHealth>;

export const ProductionModel = z.enum(['TRADITIONAL', 'HYBRID', 'AI']);
export type ProductionModel = z.infer<typeof ProductionModel>;

export const ProductionFormat = z.enum([
  'FEATURE_FILM',
  'SERIES',
  'COMMERCIAL',
  'MUSIC_VIDEO',
  'MICRO_CONTENT',
  'DOCUMENTARY',
  'OTHER',
]);
export type ProductionFormat = z.infer<typeof ProductionFormat>;

/**
 * ONE SPELLING OF EACH FORMAT, HERE, BECAUSE THREE COPIES ALREADY DRIFTED.
 *
 * The creation page, the project list and the search page each kept their own
 * `Record<string, string>` map. A `string` key accepts anything and demands
 * nothing, so when the enum changed, two of the three silently stopped
 * covering it: values reached the creation screen as raw snake case, and the
 * search page's picker — which is BUILT from its map — stopped offering them
 * at all.
 *
 * Typed against the enum, a missing entry is a compile error. Sentence case
 * throughout, as the designs write it.
 *
 * THE LIST IS THE OWNER'S, set 2026-09-21, and it is shorter than what came
 * before: the episode-count formats and the two short ones are gone, and
 * `MICRO_CONTENT` covers the vertical short-form work they were standing in
 * for. `TV_SERIES` became `SERIES` — a series is not necessarily television
 * any more, and the stored value now matches what is on screen. The rename was
 * free: `script_breakdown_project` exists in no deployed environment, and every
 * row in local development is `FEATURE_FILM`.
 */
export const PRODUCTION_FORMAT_LABELS: Record<ProductionFormat, string> = {
  FEATURE_FILM: 'Feature film',
  SERIES: 'Series',
  COMMERCIAL: 'Commercial',
  MUSIC_VIDEO: 'Music video',
  MICRO_CONTENT: 'Micro content',
  DOCUMENTARY: 'Documentary',
  OTHER: 'Other',
};

/**
 * The workspace's navigation tabs. Every phase shows all of them (owner decision
 * of 2026-10-29, see `TABS_BY_LIFECYCLE_PHASE`); the plan and the conversations
 * flag still remove the sections they do not include.
 */
export const WorkspaceTab = z.enum([
  'OVERVIEW',
  'BILLING',
  'REFERENCES',
  'SCRIPT_AND_SCENES',
  'LOCATIONS',
  'REQUESTS',
  'SCHEDULE',
  'CHAT',
  'FILES',
  'TEAM',
  'ACTIVITY',
]);
export type WorkspaceTab = z.infer<typeof WorkspaceTab>;

/**
 * The URL segment for each tab — the contract owns the vocabulary, not the SPA.
 *
 * These strings are in people's bookmarks, pasted into Slack and sent in
 * support tickets the moment the feature ships, so they are as much a public
 * interface as any API path. A second copy in the router would drift from this
 * one, and the drift would present as a dead link somebody saved months ago.
 *
 * Short and lowercase on purpose: `script` rather than `script_and_scenes`,
 * because a URL is read aloud and typed by hand.
 */
/**
 * What a reference IS — and the list is closed for a reason.
 *
 * A REFERENCE IS NOT A CANDIDATE. It carries no requirement, never enters
 * matching, and has no shortlist semantics, which is what makes the design's
 * promise — "inspiration only, nothing is contacted or held" — true rather than
 * merely written on the screen. LOCATION is the type that makes the distinction
 * load-bearing: saving a catalogue location here records that somebody liked it,
 * and does not tell its owner anything.
 */
export const ReferenceType = z.enum(['URL', 'IMAGE', 'FILE', 'NOTE', 'LOCATION']);
export type ReferenceType = z.infer<typeof ReferenceType>;

export const TAB_SLUGS: Readonly<Record<WorkspaceTab, string>> = Object.freeze({
  OVERVIEW: 'overview',
  BILLING: 'billing',
  REFERENCES: 'references',
  SCRIPT_AND_SCENES: 'script',
  LOCATIONS: 'locations',
  REQUESTS: 'requests',
  SCHEDULE: 'schedule',
  // `conversations` since team conversations (ADR 0021); `chat` stays an alias below,
  // because it is in bookmarks.
  CHAT: 'conversations',
  FILES: 'files',
  TEAM: 'team',
  ACTIVITY: 'activity',
});

/** Retired spellings that still resolve — a bookmark outlives a rename. */
const SLUG_ALIASES: Readonly<Record<string, WorkspaceTab>> = Object.freeze({ chat: 'CHAT' });

const SLUG_TO_TAB: Readonly<Record<string, WorkspaceTab>> = Object.freeze({
  ...SLUG_ALIASES,
  ...(Object.fromEntries(
    Object.entries(TAB_SLUGS).map(([tab, slug]) => [slug, tab as WorkspaceTab]),
  ) as Record<string, WorkspaceTab>),
});

/** `null` for an unknown slug — the caller decides whether that is a redirect
 *  or a not-found, which differs between a typo and a stale bookmark. */
export function tabFromSlug(slug: string | undefined): WorkspaceTab | null {
  if (!slug) return null;
  return SLUG_TO_TAB[slug.toLowerCase()] ?? null;
}

/**
 * A project's address in the browser: `northern-light-7fq2k9xd`.
 *
 * THE RANDOM KEY IS THE IDENTITY; THE NAME IS DECORATION. That split is the
 * whole design, and it buys the one property a title-derived slug cannot have:
 * a producer can rename a production and every link anybody has already shared
 * keeps working, because resolution never reads the name part. No slug history
 * table, no redirect chain, no stale `slug` column to keep in step with a title
 * that changes — the URL simply starts showing the new name.
 *
 * The key is stored (`script_breakdown_project.url_key`, unique); the name part
 * is computed from the title at render time and is never persisted.
 *
 * WHY NOT THE ULID. `01M2SWF4VJXJZQ7TXH570PBGM7` says nothing, survives no
 * glance, and is impossible to recognise in a browser history, a chat message
 * or a bug report — which is exactly where these URLs end up.
 */
export const PROJECT_URL_KEY_LENGTH = 8;

/** Lowercase alphanumerics, so the key can never be mistaken for a word boundary. */
const URL_KEY_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

const URL_KEY_PATTERN = new RegExp(`^[a-z0-9]{${String(PROJECT_URL_KEY_LENGTH)}}$`);

/** A ULID as this service mints them — Crockford base32, always 26 characters. */
const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export function mintProjectUrlKey(random: () => number = Math.random): string {
  let key = '';
  for (let i = 0; i < PROJECT_URL_KEY_LENGTH; i += 1) {
    key += URL_KEY_ALPHABET.charAt(Math.floor(random() * URL_KEY_ALPHABET.length));
  }

  return key;
}

/**
 * The name half. Latin letters and digits only, so a Cyrillic or CJK title
 * produces an EMPTY name part rather than a percent-encoded mess — in which
 * case the URL is just the key, which still works and still reads better than
 * a ULID.
 */
export function projectNameSlug(title: string): string {
  return title
    .normalize('NFKD')
    // Strip combining marks, so "Café" becomes "cafe" rather than losing the e.
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    // A trailing dash from the slice would produce a double dash before the key.
    .replace(/-+$/, '');
}

/** The full address. */
export function projectSlug(title: string, urlKey: string): string {
  const name = projectNameSlug(title);

  return name === '' ? urlKey : `${name}-${urlKey}`;
}

/**
 * Read a URL segment back into whatever identifies a project.
 *
 * Accepts THREE shapes on purpose, because all three exist in the wild:
 * a full slug, a bare key, and a bare ULID — the last because every link minted
 * before this change carries one, and an invitation accept still lands on an
 * id. A URL that worked last month must not stop working because the address
 * format improved.
 */
export function parseProjectAddress(
  segment: string | undefined,
): { urlKey: string } | { projectId: string } | null {
  if (segment === undefined || segment === '') return null;

  if (ULID_PATTERN.test(segment)) return { projectId: segment };

  // The key is the last dash-separated part, and it never contains a dash.
  const tail = segment.slice(segment.lastIndexOf('-') + 1).toLowerCase();

  return URL_KEY_PATTERN.test(tail) ? { urlKey: tail } : null;
}

/** Sub-views that also deserve their own URL. A producer comparing locations or
 *  reading a stripboard is in a place, and a place has an address. */
export const SCRIPT_SUB_SLUGS = Object.freeze({
  SCRIPT: 'script',
  REVIEW: 'review',
  /**
   * The design-19 review screen: the whole screenplay scene by scene, with what
   * the model read highlighted in the text beside the breakdown it produced.
   *
   * It sits BESIDE `review` rather than replacing it, deliberately and
   * temporarily: `review` owns approve and dismiss, and swapping it out before
   * those verbs exist here would trade a better layout for lost functionality.
   * It replaces `review` once approve, dismiss, merge and split are wired.
   */
  BREAKDOWN: 'breakdown',
  REQUIREMENTS: 'requirements',
} as const);

export const SCHEDULE_SUB_SLUGS = Object.freeze({
  CALENDAR: 'calendar',
  STRIPBOARD: 'stripboard',
  DOOD: 'dood',
  RED_FLAGS: 'red-flags',
} as const);

/**
 * Which sections a phase shows: ALL OF THEM, in every phase.
 *
 * OWNER DECISION, 2026-10-29: for now no phase hides a section. A producer at
 * Idea opened the project, looked for Locations and found no menu for it; the
 * phase is a statement about the production, and making a producer change it
 * first to reach a section turned that statement into a navigation step. The
 * same reasoning had already put Script & Scenes in every phase (owner ruling,
 * 2026-09-18); this extends it to Locations, References, Requests and Schedule.
 *
 * The map stays, so a later decision to narrow a phase is one edit here and the
 * SPA and its tests follow. It is not a permission and never was: the API checks
 * membership, role and plan whatever a phase renders. What still removes a tab is
 * not the phase: the plan (Schedule needs scheduling, Files needs media storage)
 * and the conversations flag (`WorkspacePage`).
 */
const EVERY_TAB: readonly WorkspaceTab[] = Object.freeze([...WorkspaceTab.options]);

export const TABS_BY_LIFECYCLE_PHASE: Readonly<Record<LifecyclePhase, readonly WorkspaceTab[]>> =
  Object.freeze({
    IDEA: EVERY_TAB,
    DEVELOPMENT: EVERY_TAB,
    PRE_PRODUCTION: EVERY_TAB,
    PRODUCTION: EVERY_TAB,
    WRAP: EVERY_TAB,
    // Archived still shows everything. Team conversations stay readable and refuse new
    // messages there (ADR 0021); that is the archive's rule, not a hidden section.
    ARCHIVED: EVERY_TAB,
  });

/**
 * Allowed lifecycle transitions. Backward moves are permitted — a production
 * genuinely can fall back from pre-production to development — but arbitrary
 * jumps are not. ARCHIVED is reachable from anywhere and returns only to the
 * phase it was archived from, which the API records separately.
 */
export const ALLOWED_LIFECYCLE_TRANSITIONS: Readonly<
  Record<LifecyclePhase, readonly LifecyclePhase[]>
> = Object.freeze({
  IDEA: ['DEVELOPMENT', 'ARCHIVED'],
  DEVELOPMENT: ['IDEA', 'PRE_PRODUCTION', 'ARCHIVED'],
  PRE_PRODUCTION: ['DEVELOPMENT', 'PRODUCTION', 'ARCHIVED'],
  PRODUCTION: ['PRE_PRODUCTION', 'WRAP', 'ARCHIVED'],
  WRAP: ['PRODUCTION', 'ARCHIVED'],
  ARCHIVED: ['IDEA', 'DEVELOPMENT', 'PRE_PRODUCTION', 'PRODUCTION', 'WRAP'],
} as const);

export function isLifecycleTransitionAllowed(
  from: LifecyclePhase,
  to: LifecyclePhase,
): boolean {
  if (from === to) return true;
  return ALLOWED_LIFECYCLE_TRANSITIONS[from].includes(to);
}

/**
 * `effective_health = health_override ?? derived_health`. Kept as a function so
 * no caller re-implements the precedence and quietly disagrees with the API.
 */
export function effectiveHealth(
  derived: ProjectHealth,
  override: ProjectHealth | null | undefined,
): ProjectHealth {
  return override ?? derived;
}
